/**
 * The flashcard deck's end screen — §2.8 #78, #80.
 *
 * Extracted from `LessonFlashcardRunner`. The quiz twin of this
 * (`QuizCompletedView`) leads with a percentage; this one deliberately does
 * not. A self-rated deck has no correct/incorrect — "you rated N of M cards" is
 * the only honest headline, and the grades themselves are in the summary below.
 *
 * Rating fewer than all the cards is a normal outcome, not an error: the deck
 * completes when the LAST card is rated, and a card reached by Prev and left
 * unrated stays unrated. So `rated` can legitimately be less than
 * `cards.length`.
 */
import { ListChecks, RotateCcw } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import type { ConceptResolver } from '@/lib/practice/concepts';
import {
  gradeBadgeClasses,
  gradeLabel,
  type ConceptGrade,
  type Grade,
  type ReviewOutcomes,
  type SubmissionState,
} from '@/lib/practice/grades';
import { ConceptReviewSummary } from './ConceptReviewSummary';
import { PRACTICE_CARD } from './practiceSurface';

export interface FlashcardCompletedViewProps {
  /** How many cards the user actually rated. */
  rated: number;
  /** Cards in the deck. */
  total: number;
  /** One FSRS grade per concept — the MIN of its rated cards (#80). */
  conceptGrades: ConceptGrade[];
  resolveConcept: ConceptResolver;
  outcomes: ReviewOutcomes;
  submission: SubmissionState;
  onRestart: () => void;
  /** Every rating given, for the Again / Hard / Good / Easy breakdown. */
  ratings?: Grade[];
  /** Go on to the quiz - the natural next step after a deck. */
  onTakeQuiz?: () => void;
}

export function FlashcardCompletedView({
  rated,
  total,
  conceptGrades,
  resolveConcept,
  outcomes,
  submission,
  onRestart,
  ratings = [],
  onTakeQuiz,
}: FlashcardCompletedViewProps) {
  return (
    // Centred by auto margins, not justify-center: when the card is taller
    // than the panel, justify-center pushed its top above the scroll start,
    // out of reach (the heading and the first concepts were cut off).
    <div className='mx-auto flex h-full max-w-xl flex-col overflow-y-auto p-4 sm:p-6'>
      <div
        className={cn(
          PRACTICE_CARD,
          'my-auto flex flex-col items-center p-6 text-center'
        )}
      >
        <h2 className='mb-1 text-2xl font-bold text-foreground'>Deck done</h2>
        <p className='mb-4 text-muted-foreground'>
          You rated {rated} of {total} cards.
        </p>
        <RatingBreakdown ratings={ratings} />

        <ConceptReviewSummary
          grades={conceptGrades}
          resolveConcept={resolveConcept}
          outcomes={outcomes}
          submission={submission}
        />

        {/* What next: the quiz tests what the deck rehearsed. */}
        <div className='flex w-full flex-col gap-2 sm:flex-row-reverse'>
          {onTakeQuiz && (
            <Button onClick={onTakeQuiz} className='h-11 sm:h-9 sm:flex-1'>
              <ListChecks className='mr-2 h-4 w-4' aria-hidden='true' />
              Take the quiz
            </Button>
          )}
          <Button
            onClick={onRestart}
            variant={onTakeQuiz ? 'outline' : 'default'}
            className='h-11 sm:h-9 sm:flex-1'
          >
            <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
            Review again
          </Button>
        </div>
      </div>
    </div>
  );
}

/** How the ratings fell: one chip per grade that was used, with its count. */
function RatingBreakdown({ ratings }: { ratings: Grade[] }) {
  if (ratings.length === 0) return null;
  const counts = ([1, 2, 3, 4] as Grade[])
    .map(g => [g, ratings.filter(r => r === g).length] as const)
    .filter(([, n]) => n > 0);
  return (
    <ul
      className='mb-5 flex flex-wrap justify-center gap-2'
      aria-label='Your ratings'
    >
      {counts.map(([g, n]) => (
        <li
          key={g}
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-medium',
            gradeBadgeClasses(g)
          )}
        >
          {gradeLabel(g)} <span className='font-mono'>{n}</span>
        </li>
      ))}
    </ul>
  );
}
